import React, { useEffect, useState } from "react";
import { getAvailability } from "../../../../network/api/users/availibility/availability";
import { toast } from "react-toastify";
import { useLoader } from "../../../../context/LoadingContext";
import { assets } from "../../../../assets/assets";
import DateUtils from "../../../../utils/DateUtils";
import { AvailabilityStatus, Slot } from "../../../../dto/interfaces";

interface SlotSelectionProps {
  slots: Slot[];
  setSlots: React.Dispatch<React.SetStateAction<Slot[]>>;
  selectedDate: string;
  setSelectedDate: React.Dispatch<React.SetStateAction<string>>;
  selectedSlot: Slot | null;
  setSelectedSlot: React.Dispatch<React.SetStateAction<Slot | null>>;
  serviceId: string;
  doctorId: string;
}

const SlotSelection: React.FC<SlotSelectionProps> = ({
  slots,
  setSlots,
  selectedDate,
  setSelectedDate,
  selectedSlot,
  setSelectedSlot,
  serviceId,
  doctorId,
}) => {
  const { setLoading } = useLoader();
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  async function fetchSlots(date) {
    try {
      setLoading(true);
      const res: any = await getAvailability({
        doctorId: doctorId,
        startDate: date,
        endDate: date,
        appointmentTypeId: serviceId,
      });

      setSlots(
        res?.availability.map((ele: any, index: number) => ({
          id: index,
          start: ele?.start,
          end: ele?.end,
          status: ele?.status,
        }))
      );
    } catch (error) {
      toast("An error has occured");
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (selectedDate) {
      const date = selectedDate;
      fetchSlots(date);
    }
  }, [selectedDate]);

  const getDaysInMonth = (month: number, year: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number): number => {
    return new Date(year, month, 1).getDay();
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const handlePrevMonth = () => {
    setCurrentMonth((prevMonth) => {
      const newMonth = prevMonth === 0 ? 11 : prevMonth - 1;
      if (newMonth === 11) {
        setCurrentYear((prevYear) => prevYear - 1);
      }
      return newMonth;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth((prevMonth) => {
      const newMonth = prevMonth === 11 ? 0 : prevMonth + 1;
      if (newMonth === 0) {
        setCurrentYear((prevYear) => prevYear + 1);
      }
      return newMonth;
    });
  };

  const handleDateSelect = (day: string) => {
    setSelectedDate(day);
    setSelectedSlot(null);
  };

  const generateCalendarDays = (): JSX.Element[] => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days: JSX.Element[] = [];
    const today = DateUtils.getISODate(new Date());

    // Fill empty days at the start of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="" />);
    }

    // Generate calendar days
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = DateUtils.createISODate(
        day,
        currentMonth + 1,
        currentYear
      );

      const isSelected = selectedDate === currentDate;
      const isValidDate = currentDate == today || currentDate >= today;

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(currentDate)}
          disabled={!isValidDate}
          className={` w-full h-full text-sm aspect-square flex items-center justify-center rounded-lg
            ${
              isSelected
                ? "border-black bg-gray-200"
                : "hover:border-black hover:bg-gray-50"
            }
            ${isSelected ? "border-2" : ""}
            ${!isValidDate ? "text-gray-300 cursor-not-allowed" : ""}
          `}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="w-full p-2">
      <div className="flex flex-col gap-4">
        <div className="text-xl text-center md:text-left font-semibold text-neutral-800">
          Book Your Appointment - (IST)
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="border rounded md:min-w-[40%]">
            <h2 className="text-lg sm:text-xl px-6 py-4 bg-neutral-100 w-full font-medium mb-3 sm:mb-4">
              Select A Date
            </h2>
            <div className="px-4">
              <div className="flex justify-center items-center">
                <div className="mb-4 flex items-center gap-3 justify-between">
                  <button
                    onClick={handlePrevMonth}
                    className="p-1 hover:bg-gray-100 rounded-lg"
                  >
                    <img src={assets?.left_icon} width={10} alt="next" />
                  </button>
                  <h2 className="text-lg font-semibold">
                    {monthNames[currentMonth]} {currentYear}
                  </h2>
                  <button
                    onClick={handleNextMonth}
                    className="p-1 hover:bg-gray-100 rounded-lg"
                  >
                    <img src={assets?.right_icon} width={10} alt="next" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center mb-2 ">
                {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map(
                  (day) => (
                    <div
                      key={day}
                      className="text-xs font-medium text-gray-500"
                    >
                      {day}
                    </div>
                  )
                )}
              </div>

              <div className="grid grid-cols-7 gap-1 p-1">
                {generateCalendarDays()}
              </div>
            </div>
          </div>

          <div className="w-full">
            <div className="border rounded">
              <h2 className="text-lg sm:text-xl px-6 py-4 bg-neutral-100 w-full font-medium mb-3 ">
                Choose A Slot
              </h2>
              <div className="px-6 font-semibold">
                {DateUtils.getHumanReadableDate(new Date(selectedDate))}
              </div>
              {slots?.length != 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-2 px-6 py-4">
                  {slots.map((slot) => (
                    <button
                      disabled={slot?.status != AvailabilityStatus.AVAILABLE}
                      key={slot.id}
                      className={`px-[6px] py-2 border rounded-md text-center transition duration-300 text-sm 
                             ${
                               selectedSlot?.id === slot.id
                                 ? "border-black bg-gray-200"
                                 : "border-gray-200 hover:border-black hover:bg-gray-50"
                             } ${
                        slot?.status != AvailabilityStatus.AVAILABLE
                          ? "bg-gray-100 text-gray-400 border-none"
                          : ""
                      }`}
                      onClick={() => setSelectedSlot(slot)}
                    >
                      {DateUtils.getHumanReadableTime(new Date(slot.start))}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="w-96 px-6 pb-4 text-neutral-700">
                  <div>No Slots Available !</div>
                  <div>Please Select Another day</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlotSelection;
